import React, { useEffect, useState } from "react";
import {
  Dialog as HeadlessDialog,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

const Dialog = ({
  open,
  onClose,
  title,
  children,
  width,
  maxWidth = "lg",
  maxHeight = "75vh",
  fitContent = false,
  variant = "default",
  showCloseButton = true,
  showHeaderCloseButton = false,
  closeButtonText = "Cancel",
  closeButtonAction = null,
  primaryButtonText = null,
  primaryButtonAction = null,
  footerSecondaryContent = null,
  isLoading = false,
  isPrimaryDisabled = false,
  preventOutsideClick = false,
}) => {
  const [isOpen, setIsOpen] = useState(open);

  const isDetailVariant = variant === "detail";
  const showFooter =
    primaryButtonText ||
    footerSecondaryContent ||
    (showCloseButton && !isDetailVariant);

  const maxWidthClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
    "3xl": "max-w-3xl",
    "4xl": "max-w-4xl",
    "5xl": "max-w-5xl",
  };
  const panelWidthClass = width
    ? `${width} ${isDetailVariant ? "max-w-5xl" : ""}`
    : `w-full ${maxWidthClasses[maxWidth] || maxWidthClasses.lg}`;

  useEffect(() => {
    setIsOpen(open);
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      if (closeButtonAction) {
        closeButtonAction();
      } else {
        onClose();
      }
    }, 200);
  };

  const handleDialogClose = preventOutsideClick ? () => {} : handleClose;

  return (
    <HeadlessDialog
      open={isOpen}
      onClose={handleDialogClose}
      transition
      className="relative z-[210]"
    >
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-[1px] transition duration-300 ease-out data-[closed]:opacity-0"
        aria-hidden="true"
      />

      <div className="fixed inset-0 w-screen overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          <DialogPanel
            transition
            className={`${panelWidthClass} bg-white shadow-2xl transition duration-300 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 ${
              isDetailVariant
                ? "flex max-h-[90vh] flex-col overflow-hidden rounded-xl ring-1 ring-black/5"
                : "rounded-lg"
            }`}
          >
            {title && isDetailVariant ? (
              <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50/80 px-5 py-4">
                <DialogTitle className="text-base font-semibold text-gray-900">
                  {title}
                </DialogTitle>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Close dialog"
                >
                  <i className="fi fi-rr-cross-circle text-lg leading-none" />
                </button>
              </div>
            ) : null}

            <div
              className={`min-w-0 ${
                isDetailVariant
                  ? "min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-5 py-4"
                  : fitContent
                    ? "px-4 pt-4 pb-2"
                    : "p-2"
              }`}
              style={
                isDetailVariant || fitContent
                  ? undefined
                  : { maxHeight, overflowY: "auto" }
              }
            >
              {title && !isDetailVariant ? (
                showHeaderCloseButton ? (
                  <div
                    className={`flex items-start justify-between gap-3 ${
                      fitContent ? "mb-3" : "mb-4"
                    }`}
                  >
                    <DialogTitle className="text-lg font-medium text-gray-800">
                      {title}
                    </DialogTitle>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="-mr-1 -mt-0.5 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                      aria-label="Close dialog"
                    >
                      <i className="fi fi-rr-cross-circle text-lg leading-none" />
                    </button>
                  </div>
                ) : (
                  <DialogTitle
                    className={`text-lg font-medium text-gray-800 ${
                      fitContent ? "mb-3" : "mb-4"
                    }`}
                  >
                    {title}
                  </DialogTitle>
                )
              ) : null}

              <div
                className={`max-w-full overflow-x-hidden ${
                  isDetailVariant || fitContent || !title ? "" : "mt-2"
                }`}
              >
                {children}
              </div>
            </div>

            {showFooter ? (
              <div
                className={`border-t border-gray-100 bg-gray-50/80 px-4 py-3 sm:flex sm:flex-row-reverse sm:flex-wrap sm:items-center sm:gap-y-3 ${
                  isDetailVariant ? "shrink-0 sm:px-5" : "sm:px-6"
                }`}
              >
                {primaryButtonText && (
                  <button
                    type="button"
                    className={`inline-flex min-h-[38px] w-full items-center justify-center whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold shadow-sm sm:ml-3 sm:w-auto ${
                      isLoading || isPrimaryDisabled
                        ? "bg-gray-300 text-white cursor-not-allowed"
                        : "bg-primary text-white hover:bg-primary/90"
                    }`}
                    onClick={primaryButtonAction}
                    disabled={isLoading || isPrimaryDisabled}
                  >
                    {isLoading ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Saving...</span>
                      </>
                    ) : (
                      primaryButtonText
                    )}
                  </button>
                )}

                {showCloseButton && (
                  <button
                    type="button"
                    className="mt-3 inline-flex min-h-[38px] w-full items-center justify-center whitespace-nowrap rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                    onClick={handleClose}
                  >
                    {closeButtonText}
                  </button>
                )}

                {footerSecondaryContent ? (
                  <div className="mt-3 sm:mt-0 sm:mr-3">
                    {footerSecondaryContent}
                  </div>
                ) : null}
              </div>
            ) : isDetailVariant ? (
              <p className="shrink-0 border-t border-gray-100 bg-gray-50/50 px-5 py-2 text-center text-[11px] text-gray-400">
                Press{" "}
                <kbd className="rounded border border-gray-200 bg-white px-1 py-0.5 font-mono text-[10px]">
                  Esc
                </kbd>{" "}
                to close
              </p>
            ) : null}
          </DialogPanel>
        </div>
      </div>
    </HeadlessDialog>
  );
};

export default Dialog;
